import { useQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { ReactElement } from "react";
import {
  GetNewDashboardRecognitionsQueryQuery,
  GetNewDashboardRecognitionsQueryQueryVariables,
} from "types/graphql-schema";

import Artifact from "@apps/artifact/artifact";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import { assertEdgesNonNull } from "@helpers/helpers";

import getDashboardRecognitionsQuery from "../graphql/get-dashboard-recognitions-query";

const RecognitionsCollapsibleContainer = ({
  title,
  filters,
  titleRightSide = null,
  localStorageKey,
}: {
  title: string;
  localStorageKey: keyof UiPreferenceCache;
  roundedBottom?: boolean;
  filters: GetNewDashboardRecognitionsQueryQueryVariables;
  titleRightSide?: ReactElement | null;
}) => {
  const { uiPreferenceCache } = useUiPreferenceCache();

  const { loading, data, fetchMore } = useQuery<
    GetNewDashboardRecognitionsQueryQuery,
    GetNewDashboardRecognitionsQueryQueryVariables
  >(getDashboardRecognitionsQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      after: null,
      ...filters,
      fetchData: !!uiPreferenceCache[localStorageKey],
    },
  });

  const totalCount = data?.artifacts?.totalCount;
  const recognitions = data?.artifacts?.edges
    ? assertEdgesNonNull(data.artifacts)
    : [];

  const handleClickMore = () => {
    if (data?.artifacts) {
      fetchMore({
        variables: {
          after: data.artifacts.pageInfo.endCursor,
          merge: true,
        },
      });
    }
  };

  return (
    <Layout.SidebarSubSection
      expandedUiPreferenceKey={localStorageKey}
      title={title}
      titleRightSide={titleRightSide}
      loading={recognitions.length === 0 && loading}
      count={totalCount}
    >
      <Layout.SidebarSubSectionList
        aria-label={`${title} recognition dashboard container`}
        loading={loading}
        hasNextPage={!!data?.artifacts?.pageInfo.hasNextPage}
        onClickMore={handleClickMore}
      >
        {recognitions.map(
          (recognition) =>
            recognition.__typename === "RecognitionArtifactNode" && (
              <Layout.SidebarSubSectionListItem key={recognition.id}>
                <Artifact artifact={recognition} />
              </Layout.SidebarSubSectionListItem>
            )
        )}
      </Layout.SidebarSubSectionList>
    </Layout.SidebarSubSection>
  );
};

export default Sentry.withErrorBoundary(RecognitionsCollapsibleContainer, {
  fallback: <Error description={"The recognitions could not be rendered."} />,
});
